/* eslint-disable react/prop-types */
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useCallback,
} from "react"
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  CircularProgress,
  Typography,
  Switch,
  IconButton,
  TextField,
} from "@material-ui/core"
import { Delete as DeleteIcon } from "@material-ui/icons"
import groupBy from "lodash/groupBy"

import getActive from "utils/datatables/getActive"
import { currencyOperation } from "components/invoice/utils"
import ReceiptOfInvoiceContext from "./ReceiptOfInvoiceContext"
import InvoicePicker from "./InvoicePicker"

const DOC_COLUMNS = [
  { key: "fpAsli", label: "FP Asli" },
  { key: "fpCopy", label: "FP Copy" },
  { key: "invoiceAsli", label: "Invoice Asli" },
  { key: "invoiceCopy", label: "Invoice Copy" },
  { key: "grnAsli", label: "GRN Asli" },
  { key: "doAsli", label: "DO Asli" },
  { key: "poCopy", label: "PO Copy" },
]

const ReceiptOfInvoiceDocumentTable = forwardRef(
  ({ fetchInvoiceData, classes, disabled, initialDocuments = [] }, ref) => {
    const { selectedCustomer } = useContext(ReceiptOfInvoiceContext)

    const [allInvoices, setAllInvoices] = useState([])
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [filterDate, setFilterDate] = useState("")

    const mapInvoices = (invoicesInput) => {
      // normalisasi jadi array
      // eslint-disable-next-line no-nested-ternary
      const invoices = Array.isArray(invoicesInput)
        ? invoicesInput
        : invoicesInput
        ? [invoicesInput]
        : []

      return invoices.map((inv, index) => {
        const docs = inv.documents || inv.raw?.documents || []

        const amount = inv.productDescriptions?.reduce((a, b) => {
          const unitPrice = currencyOperation(
            b.unitPrice,
            inv.rateIn,
            inv.currencyRate
          )
          const percentage = b.paymentPercentage ?? inv.paymentPercentage
          if (!percentage) {
            return a + unitPrice * b.quantity
          }
          return a + unitPrice * (0 + percentage / 100)
        }, 0)

        const baseAmount = amount - inv.discount
        let totalCost = Math.floor(baseAmount * (1 + (inv?.vat / 100 ?? 1)))
        if (inv?.vat === 12) {
          totalCost = Math.round(
            baseAmount + (((11 / inv.vat) * baseAmount * inv.vat) / 100 ?? 1)
          )
        }

        if (Number.isNaN(totalCost)) {
          totalCost = inv?.totalAmount || 0
        }

        const normalize = (s) => (s || "").toLowerCase().trim()
        const hasDoc = (name) =>
          docs.some((d) => normalize(d.documentName) === normalize(name))
        return {
          raw: inv,
          id:
            inv.idInvoice ||
            inv.idReceiptOfInvoiceDetail ||
            inv.id ||
            `row-${index + 1}`,

          invoiceNumber: inv.invoiceNumber || inv.invoice?.invoiceNumber || "",
          invoiceDate: inv.invoiceDate || inv.date || inv.invoice?.date || "",
          currency:
            inv.currency ||
            inv.currencyCode ||
            inv.currency?.code ||
            inv.invoice?.currency?.code ||
            "",

          amount: totalCost,

          fpAsli: inv.fpAsli ?? hasDoc("FP Asli"),
          fpCopy: inv.fpCopy ?? hasDoc("FP Copy"),
          invoiceAsli: inv.invoiceAsli ?? hasDoc("Invoice Asli"),
          invoiceCopy: inv.invoiceCopy ?? hasDoc("Invoice Copy"),
          grnAsli: inv.grnAsli ?? hasDoc("GRN Asli"),
          doAsli: inv.doAsli ?? hasDoc("DO Asli"),
          poCopy: inv.poCopy ?? hasDoc("PO Copy"),
        }
      })
    }

    useEffect(() => {
      setRows(mapInvoices(initialDocuments))
    }, [initialDocuments])

    useEffect(() => {
      if (!selectedCustomer) {
        setAllInvoices([])
        // setRows([])
        return undefined
      }

      const { checkActive, cleanupEffect } = getActive()

      const load = async () => {
        setLoading(true)
        setError(null)
        try {
          const result = await fetchInvoiceData(selectedCustomer.idCust)
          const invoicesRaw = Array.isArray(result)
            ? result
            : result?.data || []

          if (!checkActive()) return

          // const mapped = mapInvoices(invoicesRaw)

          // setAllInvoices(mapped)
          // setRows(mapped)

          // const invoices = Array.isArray(result) ? result : result?.data || []

          // if (!checkActive()) return

          // setRows(mapInvoices(invoices))

          setAllInvoices(mapInvoices(invoicesRaw))
        } catch (e) {
          if (!checkActive()) return
          console.error("Failed to fetch invoices for ROI:", e)
          setError(e)
          setAllInvoices([])
          setRows([])
        } finally {
          if (checkActive()) setLoading(false)
        }
      }

      load()
      return cleanupEffect
    }, [selectedCustomer, fetchInvoiceData])

    const toggle = (rowId, field) => {
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                [field]: !r[field],
              }
            : r
        )
      )
    }

    // function buat hapus invoice dari list
    const handleRemoveRow = (rowId) => {
      setRows((prev) => prev.filter((r) => r.id !== rowId))
    }

    // function filter tanggal
    const filteredRows = useMemo(() => {
      if (!filterDate) return rows

      return rows.filter((r) => {
        if (!r.invoiceDate) return false

        return String(r.invoiceDate).startsWith(filterDate)
      })
    }, [filterDate, rows])

    useImperativeHandle(ref, () => ({
      // ikut pola child form lain: ada submitForm (walau no-op)
      submitForm: () => Promise.resolve(),
      getFormData: () => ({
        documents: filteredRows.map((r) => ({
          invoiceId:
            r.raw?.idInvoice ||
            r.raw?.invoice?.idInvoice ||
            r.raw?.idReceiptOfInvoiceDetail ||
            r.id,
          invoiceNumber: r.invoiceNumber,
          invoiceDate: r.invoiceDate,
          currency: r.currency,
          amount: r.amount,
          fpAsli: r.fpAsli,
          fpCopy: r.fpCopy,
          invoiceAsli: r.invoiceAsli,
          invoiceCopy: r.invoiceCopy,
          grnAsli: r.grnAsli,
          doAsli: r.doAsli,
          poCopy: r.poCopy,
        })),
      }),
    }))

    const unchosenInvoiceGroups = useMemo(() => {
      const chosenNumbers = new Set(rows.map((r) => r.invoiceNumber))
      const unchosen = allInvoices.filter(
        (inv) => !chosenNumbers.has(inv.invoiceNumber)
      )
      return groupBy(unchosen, "invoiceNumber")
    }, [allInvoices, rows])

    const invoicePickerOptions = useMemo(() => {
      const invoices = Object.keys(unchosenInvoiceGroups)
      return invoices.map((code) => {
        const inv = unchosenInvoiceGroups[code]
        const firstItem = inv[0]
        return {
          invoiceNumber: firstItem.invoiceNumber,
          customerName: firstItem.customerName,
          date: firstItem.date,
          totalAmount: firstItem.totalAmount,
          invoice: firstItem,
        }
      })
    }, [unchosenInvoiceGroups])

    const handleAddItem = (detail) => {
      const inv = detail.invoice
      if (!inv) return

      const rowToAdd = inv.raw ? inv : mapInvoices([inv])[0]

      setRows((prev) => {
        if (prev.some((r) => r.id === rowToAdd.id)) {
          return prev
        }
        return [...prev, rowToAdd]
      })
    }

    const fetchAllInvoicesForPicker = useCallback(
      (searchText) => {
        if (!selectedCustomer?.idCust) {
          return Promise.resolve([])
        }

        return fetchInvoiceData(selectedCustomer.idCust, searchText)
      },
      [fetchInvoiceData, selectedCustomer]
    )

    return (
      <Grid item xs={12}>
        <Paper style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16, maxWidth: 240 }}>
            <TextField
              fullWidth
              label="Filter tanggal invoice"
              type="date"
              variant="outlined"
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </div>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {/* <TableCell>No</TableCell> */}
                  <TableCell>Nomor Invoice</TableCell>
                  <TableCell>Tanggal</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  {DOC_COLUMNS.map((col) => (
                    <TableCell key={col.key} align="center">
                      {col.label}
                    </TableCell>
                  ))}
                  <TableCell align="center">Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow key={row.id}>
                    {/* <TableCell>{idx + 1}</TableCell> */}
                    <TableCell>{row.invoiceNumber}</TableCell>
                    <TableCell>{row.invoiceDate}</TableCell>
                    {/* <TableCell>{row.currency}</TableCell> */}
                    <TableCell>
                      {row.currency?.code ?? row.currency ?? "-"}
                    </TableCell>
                    <TableCell align="right">
                      {typeof row.amount === "number"
                        ? row.amount.toLocaleString("id-ID")
                        : row.amount}
                    </TableCell>
                    {DOC_COLUMNS.map((col) => (
                      <TableCell key={col.key} align="center">
                        <Switch
                          color="primary"
                          checked={row[col.key]}
                          onChange={() => toggle(row.id, col.key)}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveRow(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5 + DOC_COLUMNS.length + 1}
                      align="center"
                    >
                      <Typography variant="body2">
                        {selectedCustomer
                          ? "Tidak ada invoice untuk customer ini."
                          : "Silakan pilih customer terlebih dahulu."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow id="add-row">
                  <TableCell colSpan={11}>
                    <InvoicePicker
                      classes={classes}
                      disabled={disabled}
                      data={invoicePickerOptions}
                      onAddItem={handleAddItem}
                      fetchInvoiceData={fetchAllInvoicesForPicker}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

          {loading && (
            <div style={{ padding: 16, textAlign: "center" }}>
              <CircularProgress size={24} />
            </div>
          )}

          {error && !loading && (
            <div
              style={{
                padding: 16,
                textAlign: "center",
                color: "red",
              }}
            >
              Gagal memuat data invoice.
            </div>
          )}
        </Paper>
      </Grid>
    )
  }
)

export default ReceiptOfInvoiceDocumentTable
